import { useMemo } from "react";
import { useGetBulkAreaStatistics } from "../api/statisticsQueries";

export interface ContentItemWithStats {
  content: Area | Model;
  contentType: "area" | "model";
  statistics?: {
    route_count: number;
    min_grade: number | null;
    max_grade: number | null;
    grade_distribution?: { [grade: number]: number };
  };
}

/**
 * Hook to efficiently fetch and combine statistics for homepage content.
 * This hook will batch requests for areas to minimize API calls.
 */
export const useHomepageStatistics = (
  content: (Area | Model)[],
  getContentType: (item: Area | Model) => "area" | "model"
) => {
  // Extract area IDs from the content
  const areaIds = useMemo(() => {
    const areas: string[] = [];

    content.forEach((item) => {
      const type = getContentType(item);
      if (type === "area") {
        areas.push(item.id);
      }
    });

    return areas;
  }, [content, getContentType]);

  // Fetch bulk statistics
  const {
    data: areaStats,
    isLoading: isAreaStatsLoading,
    error: areaStatsError,
  } = useGetBulkAreaStatistics(areaIds, {
    enabled: areaIds.length > 0,
  });

  // Combine content with statistics
  const contentWithStats: ContentItemWithStats[] = useMemo(() => {
    return content.map((item) => {
      const contentType = getContentType(item);
      let statistics: ContentItemWithStats["statistics"];

      if (contentType === "area" && areaStats?.[item.id]) {
        const stats = areaStats[item.id];
        statistics = {
          route_count: stats.route_count,
          min_grade: stats.min_grade,
          max_grade: stats.max_grade,
          grade_distribution: stats.grade_distribution,
        };
      }

      return {
        content: item,
        contentType,
        statistics,
      };
    });
  }, [content, getContentType, areaStats]);

  return {
    contentWithStats,
    isLoading: isAreaStatsLoading,
    error: areaStatsError,
    hasStatistics: Boolean(areaStats),
  };
};

/**
 * Utility function to format grade range for display
 */
export const formatGradeRange = (
  minGrade: number | null,
  maxGrade: number | null,
  _gradePreference?: string
): string | null => {
  if (minGrade === null || maxGrade === null) {
    return null;
  }

  if (minGrade === maxGrade) {
    // Single grade - you might want to use your existing grade conversion utilities here
    return `${minGrade}`;
  }

  return `${minGrade}-${maxGrade}`;
};

/**
 * Utility function to get a summary of the grade distribution
 */
export const getGradeDistributionSummary = (gradeDistribution: {
  [grade: number]: number;
}): {
  totalRoutes: number;
  mostCommonGrade: number | null;
  gradeSpread: number;
} => {
  const grades = Object.keys(gradeDistribution).map(Number);
  const totalRoutes = Object.values(gradeDistribution).reduce(
    (sum, count) => sum + count,
    0
  );

  if (grades.length === 0) {
    return {
      totalRoutes: 0,
      mostCommonGrade: null,
      gradeSpread: 0,
    };
  }

  // Find most common grade
  let mostCommonGrade = grades[0];
  let maxCount = gradeDistribution[mostCommonGrade];

  for (const grade of grades) {
    if (gradeDistribution[grade] > maxCount) {
      maxCount = gradeDistribution[grade];
      mostCommonGrade = grade;
    }
  }

  // Calculate grade spread
  const minGrade = Math.min(...grades);
  const maxGrade = Math.max(...grades);
  const gradeSpread = maxGrade - minGrade;

  return {
    totalRoutes,
    mostCommonGrade,
    gradeSpread,
  };
};
